/**
 * The editable-director field vocabulary, shared by the Management Profile tab (inline cell
 * editing) and the Overview director drawer's Edit Details modal. It lives here rather than in
 * either module because those two are sibling DashboardModules, and the row-to-UW field mapping
 * has to stay identical on both sides — a save from one surface must write the same column as
 * the same save from the other.
 */

import { isMaskedValue } from '../../utils/utils';

export const PHONE_COUNTRY_PREFIX = '+91 (IND)';

export const LINKEDIN_ROW_FIELD = 'linkedinUrl';

export const LINKEDIN_ENRICHED_AT_FIELD = 'linkedinEnrichedAt';

/*
   The app-wide predicate, not a local one. A bureau PAN comes back masked as "XXXXX1234F" as
   often as "*****1234F", and underwriting's fill-only check reads the same run-of-3 rule — a
   narrower test here locks a field the server would happily let the user fill, with no way out.
*/
export const isMaskedIdentityValue = (value?: string): boolean => isMaskedValue(value);

export const MANAGEMENT_PROFILE_MASKABLE_FIELDS: ReadonlySet<string> = new Set(['phone', 'email']);

export const MANAGEMENT_PROFILE_FILL_ONLY_ROW_FIELDS: ReadonlySet<string> = new Set(['pan', 'din']);

export const MANAGEMENT_PROFILE_ROW_TO_UW_FIELD: Record<string, string> = {
    pan: 'primaryPan',
    din: 'din',
    email: 'email',
    phone: 'phoneNumber',
    linkedinUrl: 'linkedinUrl',
    address: 'primaryAddress',
    managementProfileSummary: 'managementProfileSummary',
};

export const LINKEDIN_REFRESH_COPY = {
    FETCHING: 'Fetching profile…',
    STALE_HINT: 'Details on this profile are from the previous URL until this finishes.',
    UNCONFIRMED: "Couldn't confirm an updated LinkedIn profile — any new details will appear on your next refresh.",
} as const;

const LINKEDIN_FETCHED_DETAIL_FIELDS = [
    'headline',
    'followersCount',
    'imageUrl',
    'locationCity',
    'websiteUrl',
    'education',
    'workExperience',
] as const;

const LINKEDIN_SCRAPED_FIELDS = [...LINKEDIN_FETCHED_DETAIL_FIELDS, 'linkedinMatchConfidence', LINKEDIN_ENRICHED_AT_FIELD];

export const stripLinkedInEnrichment = <T extends Record<string, unknown> | undefined>(customFields: T): T => {
    if (!customFields) return customFields;
    const remaining = { ...customFields };
    LINKEDIN_SCRAPED_FIELDS.forEach((field) => delete remaining[field]);
    return remaining as T;
};

export const clearLinkedInDerivedValues = <T extends Record<string, any>>(row: T): T => ({
    ...row,
    imageUrl: undefined,
    followersCount: undefined,
    subTitle: undefined,
    customFields: { ...stripLinkedInEnrichment(row.customFields), [LINKEDIN_ROW_FIELD]: '' },
});

export const hasLinkedInEnrichment = (customFields?: Record<string, any> | null): boolean =>
    !!customFields &&
    LINKEDIN_FETCHED_DETAIL_FIELDS.some((field) => {
        const value = customFields[field];
        return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
    });

export const triggersLinkedInScrape = (updates: Record<string, string>): boolean => !!updates[LINKEDIN_ROW_FIELD]?.trim();

export const FILL_ONCE_COPY = {
    HINT: 'Can only be added once and cannot be edited after saving.',
    CONFIRM_TITLE: 'PAN / DIN cannot be edited once saved. Continue?',
    CONFIRM_SUBTITLE:
        'Please review the value carefully. It will be used to identify the director for compliance checks and to prevent duplicate records.',
    CONFIRM: 'Confirm & save',
    CANCEL: 'Go back',
} as const;

export const MANAGEMENT_PROFILE_SAVED_TOAST = 'Director details updated.';

export const MASKED_VALUE_HINT = 'Partially hidden by the source — type the full value to replace it.';

export const LINKEDIN_CLEAR_CONFIRM_COPY = {
    TITLE: 'This will delete the previously fetched data. Proceed?',
    SUBTITLE:
        'Removing the LinkedIn URL also removes what was fetched from it — headline, photo, location, work experience and education. Adding a URL back will fetch details again.',
    CONFIRM: 'Proceed',
    CANCEL: 'Cancel',
} as const;

export const isIdentityCellLocked = (field?: string, row?: Record<string, any>): boolean => {
    if (!field || !MANAGEMENT_PROFILE_FILL_ONLY_ROW_FIELDS.has(field)) return false;
    const stored = row?.[field];
    return !!stored?.trim?.() && !isMaskedIdentityValue(stored);
};
